import React, { Component } from 'react';
import {
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  Badge,
  Collapse,
  ListGroup,
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faShieldAlt,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faBookOpen,
} from '@fortawesome/free-solid-svg-icons';

type GlossaryTerm = {
  term: string;
  field: string;
  definition: string;
  category: string;
  relatedTerms: string[];
  synonyms: string[];
  stewardRole: string;
  criticality: 'Low' | 'Medium' | 'High';
  complianceTags: string[];
  dqDimensions?: string[];
  stewardship_guidance?: {
    reviewFrequency?: string;
    owners?: string[];
    approvers?: string[];
  };
};

type PolicyMapping = {
  field: string;
  policy: string;
  purpose: string;
  risk_level: 'Low' | 'Medium' | 'High';
  recommendation: string;
  note: string;
};

type Props = {
  glossary: GlossaryTerm[];
  policyMappings: PolicyMapping[];
};

type State = {
  selectedCategory: string | null;
  modalVisible: boolean;
  editingTerm: GlossaryTerm | null;
  expandedRows: Set<string>;
  sidebarCollapsed: boolean;
};

export default class DataGovernanceCatalog extends Component<Props, State> {
  state: State = {
    selectedCategory: null,
    modalVisible: false,
    editingTerm: null,
    expandedRows: new Set(),
    sidebarCollapsed: false,
  };

  componentDidMount() {
    const firstCategory = this.getCategories()[0];
    if (firstCategory) {
      this.setState({ selectedCategory: firstCategory });
    }
  }

  getCategories(): string[] {
    return [...new Set(this.props.glossary.map((term) => term.category))];
  }

  getTermsForCategory(category: string): GlossaryTerm[] {
    return this.props.glossary.filter((term) => term.category === category);
  }

  getPolicyForField(field: string): PolicyMapping[] {
    return this.props.policyMappings.filter((p) => p.field === field);
  }

  getCriticalityColor(level: string) {
    switch (level) {
      case 'High':
        return 'danger';
      case 'Medium':
        return 'warning';
      case 'Low':
        return 'success';
      default:
        return 'secondary';
    }
  }

  deleteTerm = (termName: string) => {
    console.log('Delete term not implemented', termName);
  };

  toggleExpand = (term: string) => {
    const expanded = new Set(this.state.expandedRows);
    if (expanded.has(term)) expanded.delete(term);
    else expanded.add(term);
    this.setState({ expandedRows: expanded });
  };

  renderSidebar() {
    const categories = this.getCategories();
    const { sidebarCollapsed } = this.state;

    return (
      <div className={`border-end p-2 ${sidebarCollapsed ? 'text-center' : ''}`}>
        <div className="d-flex justify-content-between align-items-center mb-2">
          {!sidebarCollapsed && <h5 className="mb-0">Glossary</h5>}
          <Button
            variant="light"
            size="sm"
            onClick={() => this.setState({ sidebarCollapsed: !sidebarCollapsed })}
          >
            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
          </Button>
        </div>
        <ul className="list-group">
          {categories.map((cat) => (
            <li
              key={cat}
              className={`list-group-item ${
                this.state.selectedCategory === cat ? 'active' : ''
              }`}
              style={{ cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              title={cat}
              onClick={() => this.setState({ selectedCategory: cat })}
            >
              <FontAwesomeIcon icon={faBookOpen} className="me-2" />
              {!sidebarCollapsed && cat}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  renderTermRow(term: GlossaryTerm) {
    const isExpanded = this.state.expandedRows.has(term.term);
    const policies = this.getPolicyForField(term.field);

    return (
      <>
        <tr key={term.term}>
          <td>
            <span
              style={{ cursor: 'pointer' }}
              onClick={() => this.toggleExpand(term.term)}
            >
              <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="me-2" />
              <strong>{term.term}</strong>
            </span>
          </td>
          <td>{term.definition}</td>
          <td>
            <Badge bg={this.getCriticalityColor(term.criticality)}>
              {term.criticality}
            </Badge>
          </td>
          <td>{term.stewardRole}</td>
          <td>
            {term.complianceTags.map((tag) => (
              <Badge key={tag} bg="secondary" className="me-1">
                {tag}
              </Badge>
            ))}
          </td>
          <td>
            {term.dqDimensions?.map((d) => (
              <Badge key={d} bg="secondary" className="me-1">
                {d}
              </Badge>
            ))}
          </td>
          <td>
            {policies.length > 0 ? (
              policies.map((p, i) => (
                <div key={i} title={p.purpose}>
                  <FontAwesomeIcon icon={faShieldAlt} className="text-primary me-1" />
                  {p.policy}
                </div>
              ))
            ) : (
              '—'
            )}
          </td>
          <td>
            <FontAwesomeIcon
              icon={faEdit}
              className="me-2 text-success"
              style={{ cursor: 'pointer' }}
              onClick={() => this.setState({ editingTerm: term, modalVisible: true })}
            />
            <FontAwesomeIcon
              icon={faTrash}
              className="text-danger"
              style={{ cursor: 'pointer' }}
              onClick={() => this.deleteTerm(term.term)}
            />
          </td>
        </tr>
        <tr>
          <td colSpan={8} className="p-0 border-top-0">
            <Collapse in={isExpanded}>
              <div className="bg-light p-3 border-top">
                <strong>Synonyms:</strong> {term.synonyms.join(', ') || 'None'}<br />
                <strong>Related Terms:</strong> {term.relatedTerms.join(', ') || 'None'}<br />

                {term?.stewardship_guidance && (
                  <div className="my-3">
                    <h6>🧭 Stewardship Guidance</h6>
                    <ul>
                      {term?.stewardship_guidance.reviewFrequency && <li><strong>Review Frequency:</strong> {term?.stewardship_guidance.reviewFrequency}</li>}
                      {term?.stewardship_guidance.owners?.length && <li><strong>Owners:</strong> {term?.stewardship_guidance.owners.join(', ')}</li>}
                      {term?.stewardship_guidance.approvers?.length && <li><strong>Approvers:</strong> {term?.stewardship_guidance.approvers.join(', ')}</li>}
                    </ul>
                  </div>
                )}

                {policies.length > 0 && (
                  <>
                    <hr />
                    <h6 className="mb-1">📜 Linked Policies</h6>
                    <ListGroup variant="flush">
                      {policies.map((p, i) => (
                        <ListGroup.Item key={i} className="bg-light border-0 px-0 pb-2">
                          <strong>{p.policy}</strong><br />
                          <small className="text-muted">Purpose:</small> {p.purpose || 'N/A'}<br />
                          <small className="text-muted">Risk Level:</small> {p.risk_level || 'N/A'}<br />
                          <small className="text-muted">Recommendation:</small> {p.recommendation || 'N/A'}<br />
                          {p.note && <small className="text-muted">Note:</small>} {p.note}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </>
                )}
              </div>
            </Collapse>
          </td>
        </tr>
      </>
    );
  }

  renderMainTable() {
    const { selectedCategory } = this.state;
    if (!selectedCategory)
      return <div>Select a category to view glossary terms.</div>;

    const terms = this.getTermsForCategory(selectedCategory);

    return (
      <>
        <h5>{selectedCategory} Terms</h5>
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>Term</th>
              <th>Definition</th>
              <th>Criticality</th>
              <th>Steward</th>
              <th>Tags</th>
              <th>DQ Dimensions</th>
              <th>Policy</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{terms.map((term) => this.renderTermRow(term))}</tbody>
        </Table>
      </>
    );
  }

  renderEditModal() {
    const { editingTerm } = this.state;
    if (!editingTerm) return null;

    return (
      <Modal
        show={this.state.modalVisible}
        onHide={() => this.setState({ modalVisible: false, editingTerm: null })}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Glossary Term</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Term</Form.Label>
              <Form.Control type="text" value={editingTerm.term} readOnly />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Definition</Form.Label>
              <Form.Control as="textarea" value={editingTerm.definition} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Category</Form.Label>
              <Form.Control type="text" value={editingTerm.category} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Steward Role</Form.Label>
              <Form.Control type="text" value={editingTerm.stewardRole} />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Criticality</Form.Label>
              <Form.Select value={editingTerm.criticality}>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Compliance Tags</Form.Label>
              <Form.Control
                type="text"
                placeholder="Comma separated"
                value={editingTerm.complianceTags.join(', ')}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => this.setState({ modalVisible: false, editingTerm: null })}
          >
            Cancel
          </Button>
          <Button variant="primary">Save Changes</Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    return (
      <div className="container-fluid">
        <Row>
          <Col md={this.state.sidebarCollapsed ? 1 : 2} style={{ borderRight: '1px solid #dee2e6', height: '100vh', overflowY: 'auto' }}>
            {this.renderSidebar()}
          </Col>
          <Col md={this.state.sidebarCollapsed ? 11 : 10} className="p-4" style={{ height: '100vh', overflowY: 'auto' }}>
            {this.renderMainTable()}
          </Col>
        </Row>
        {this.renderEditModal()}
      </div>
    );
  }
}
